import { useCallback, useRef, useState } from 'react';

export interface UseControllableStateOptions<T> {
  /** Controlled value from props. Pass `undefined` for uncontrolled mode. */
  value?: T;
  /** Initial value when uncontrolled. */
  defaultValue: T;
  /** Fired after any value change (both controlled and uncontrolled). */
  onChange?: (value: T) => void;
}

/** Bridges the controlled / uncontrolled duality used by all form
 *  components (Input, Select, Tabs, Accordion, Switch, etc.) so each
 *  component can work in either mode without branching internally.
 *
 *  Invariant: whether the hook returns controlled or uncontrolled
 *  behaviour is decided on FIRST RENDER only. Switching the `value`
 *  prop from `undefined` to defined (or vice versa) mid-flight is a
 *  user error and not supported. */
export function useControllableState<T>(
  options: UseControllableStateOptions<T>,
): [T, (next: T) => void] {
  const { value, defaultValue, onChange } = options;
  const isControlledRef = useRef<boolean>(value !== undefined);
  const [internal, setInternal] = useState<T>(defaultValue);
  const current = isControlledRef.current ? (value as T) : internal;

  const setValue = useCallback(
    (next: T) => {
      if (!isControlledRef.current) {
        setInternal(next);
      }
      onChange?.(next);
    },
    [onChange],
  );

  return [current, setValue];
}
